import type { ProductJourney } from "@shared/analytics/productEvents";
import {
    useProductJourney,
    useProductObservation
} from "@shared/analytics/useProductJourney";
import { ArrowShortLeftIcon } from "@solomon-labs/design/components/icons";
import { Button, Text } from "@solomon-labs/design/components/ui";
import { motion } from "motion/react";
import React from "react";

import { DistributionModeField } from "../application/DistributionModeField";
import type { DistributionMode } from "../application/types";

/**
 * In-page "Switch Distribution" form. Opened from the footer's
 * Switch-mode toggle link; replaces the right-column card with a
 * Distribution field + Verify Ownership CTA. Back cancels without
 * writing the parent's `manageDistributionMode`.
 */
export function SwitchDistributionModePanel({
    analytics: entryAnalytics,
    walletAddress,

    stagedMode,
    setStagedMode,
    committedMode,
    onBack,
    onCommit,
    canRetryWithMicroTransfer = false,
    isCommitting = false,
    commitError = null
}: {
    analytics?: ProductJourney;
    walletAddress?: string;
    /** Local staged value — the user can flip back and forth before
     *  committing without writing the parent's `manageDistributionMode`. */
    stagedMode: DistributionMode;
    setStagedMode: (next: DistributionMode) => void;
    /** Current committed mode. Submit is disabled when staged === committed
     *  so the user can only commit a real change. */
    committedMode: DistributionMode;
    onBack: () => void;
    onCommit: (
        analytics: ProductJourney,
        preferredMethod?: "microTransfer"
    ) => void;
    canRetryWithMicroTransfer?: boolean;
    /** True while the real verify-then-update ceremony is in flight —
     *  disables the CTA and swaps its copy. */
    isCommitting?: boolean;
    /** Ceremony failure message; rendered below the CTA. */
    commitError?: string | null;
}): React.ReactElement {
    const localAnalytics = useProductJourney(
        "rewards_distribution",
        "rewards_footer",
        walletAddress
    );
    const analytics = entryAnalytics ?? localAnalytics;
    useProductObservation(analytics, "form_viewed", {
        previous_mode: committedMode
    });
    useProductObservation(analytics, "validation_updated", {
        can_submit: stagedMode !== committedMode && !isCommitting,
        request_pending: isCommitting
    });
    return (
        <motion.div
            key="switch-mode"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{
                opacity: { duration: 0.3, ease: "easeOut" },
                y: { duration: 0.3, ease: "easeOut" }
            }}
            className="flex flex-col gap-3"
        >
            <Button
                variant="link"
                size="sm"
                onClick={() => {
                    analytics.close("back");
                    onBack();
                }}
                className="self-start"
            >
                <ArrowShortLeftIcon className="size-4" />
                Back
            </Button>
            <Text
                variant="h1"
                render={<span />}
                className="text-foreground-base"
            >
                Switch Distribution
            </Text>
            <div className="mt-4 h-px bg-line-base" />
            <div className="mt-6">
                <DistributionModeField
                    value={stagedMode}
                    onChange={(next) => {
                        analytics.send("selected", {
                            distribution_mode: next,
                            previous_mode: stagedMode
                        });
                        setStagedMode(next);
                    }}
                    streamLabel="Stream"
                    claimLabel="Claim"
                    streamDescription="Switching to Stream automatically claims all pending rewards. Future rewards stream to your wallet."
                    claimDescription="User will claim rewards when they want"
                    showLabel={false}
                    selectOneHint="below-right"
                />
            </div>
            <Button
                variant="raised"
                interaction="light-bar"
                size="lg"
                fullWidth
                className="mt-15"
                disabled={stagedMode === committedMode || isCommitting}
                onClick={() => onCommit(analytics)}
            >
                {isCommitting ? "Verifying…" : "Verify Ownership to Update"}
            </Button>
            {canRetryWithMicroTransfer && stagedMode !== committedMode && (
                <Button
                    variant="link"
                    size="sm"
                    className="self-start"
                    disabled={isCommitting}
                    onClick={() => onCommit(analytics, "microTransfer")}
                >
                    Verify with a small transfer instead
                </Button>
            )}
            {commitError && (
                <Text
                    variant="small"
                    render={<p />}
                    className="text-error-base"
                >
                    {commitError}
                </Text>
            )}
        </motion.div>
    );
}
